/**
 * Client-side billing helpers — callable-function wrappers and redirect utilities.
 *
 * The actual Stripe flow lives on the marketing site (`zoeyorganizer.com`).
 * From the web app we only ever:
 *   1. Mint a short-lived custom token via `generateBillingHandoffToken`
 *   2. `window.location.href` to `/billing` (Customer Portal) or `/pricing`
 *      (Checkout) with the token in the query string
 *
 * See [[specs/stripe-billing-and-marketing-site-portal]] "Handoff URL flow".
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import getApp from "@/lib/firebase";
import { MARKETING_ORIGIN, buildMarketingUrl } from "@/lib/subscription";
import type { Cadence, PricingTier } from "@/lib/pricingTiers";

export type HandoffReturnPath = "/billing" | "/pricing";

/**
 * Calls the `generateBillingHandoffToken` Cloud Function and returns the URL
 * (including the signed `t` param) the client should redirect to.
 *
 * The callable itself accepts `{ return_path }` and may return either
 * `{ url }` (preferred) or `{ token }` (legacy fallback) — we handle both.
 */
export async function getBillingHandoffUrl(
  return_path: HandoffReturnPath,
): Promise<string> {
  const functions = getFunctions(getApp());
  const callable = httpsCallable<
    { return_path: HandoffReturnPath },
    { url?: string; token?: string }
  >(functions, "generateBillingHandoffToken");

  const result = await callable({ return_path });
  const data = result.data ?? {};
  if (data.url) return data.url;
  if (data.token) return buildMarketingUrl(return_path, data.token);
  throw new Error("generateBillingHandoffToken returned no url or token");
}

/**
 * Open the Stripe Customer Portal directly via the `createBillingPortalSession`
 * callable. When a `householdId` is provided, the household-scoped path is
 * used (any adult member can open the portal for the household's covering
 * subscription, per Decision #7 in
 * [[shared-subscription-any-household-adult-can-pay-co]]). Without one, the
 * legacy caller-keyed path is used (only works for payers).
 *
 * The portal lives on Stripe's domain (`billing.stripe.com`); no marketing-
 * site handoff is required for this redirect. The marketing-site `/billing`
 * page still uses the legacy handoff for direct-visit traffic.
 */
export async function redirectToBillingPortal(opts?: {
  householdId?: string;
}): Promise<void> {
  const functions = getFunctions(getApp());
  const callable = httpsCallable<
    {
      household_id?: string;
      return_url: string;
    },
    { url: string }
  >(functions, "createBillingPortalSession");

  const result = await callable({
    ...(opts?.householdId ? { household_id: opts.householdId } : {}),
    return_url: window.location.href,
  });
  const url = result.data?.url;
  if (!url) {
    throw new Error("createBillingPortalSession returned no url");
  }
  window.location.href = url;
}

/** Redirect the user to the marketing `/pricing` page for Checkout. */
export async function redirectToPricing(): Promise<void> {
  const url = await getBillingHandoffUrl("/pricing");
  window.location.href = url;
}

/**
 * Calls `createCheckoutSession` with the selected tier + cadence and redirects
 * the user to the Stripe-hosted Checkout URL. Used for new subscribers who
 * don't yet have a Stripe customer record.
 *
 * On success, `window.location.href` navigates away — the caller's component
 * will unmount before this function resolves. On error, the promise rejects
 * and the caller is responsible for surfacing the failure.
 */
export async function redirectToStripeCheckout(args: {
  tier: PricingTier["id"];
  cadence: Cadence;
  householdId: string;
}): Promise<void> {
  const functions = getFunctions(getApp());
  const callable = httpsCallable<
    {
      tier: string;
      cadence: Cadence;
      household_id: string;
      success_url: string;
      cancel_url: string;
    },
    { url: string }
  >(functions, "createCheckoutSession");

  const result = await callable({
    tier: args.tier,
    cadence: args.cadence,
    household_id: args.householdId,
    success_url: `${MARKETING_ORIGIN}/checkout/success`,
    cancel_url: `${MARKETING_ORIGIN}/checkout/cancel`,
  });

  const url = result.data?.url;
  if (!url) {
    throw new Error("createCheckoutSession returned no url");
  }
  window.location.href = url;
}

export { MARKETING_ORIGIN };
